import React, { useState, useRef, useEffect } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CloseButton,
  Link,
  FormControl,
  FormLabel,
  useToast,
  Heading,
  Input,
  Text,
  Select,
  Grid,
  GridItem,
  Image,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  RadioGroup,
  Radio,
  Stack,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons"; // Import AddIcon
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import LogoComponent from "../Logo/LogoComponent";
import { cqAPI } from "../../lib/Auth";
import TagManager from "react-gtm-module";
import { EmailIcon } from "@chakra-ui/icons";

// SS-4111: the optional SMS opt-in. The words must stay identical to the api's
// SMS_CONSENT_DISCLOSURES[SMS_CONSENT_VERSION] (essentials/models/OutreachSmsConsent.js),
// which is what the opt-in record stores; change both together under a new version.
export const SMS_CONSENT_VERSION = "2026-10-20";
const TERMS_URL = "https://www.getviva.ai/terms-of-service-log/";
const PRIVACY_URL = "https://www.getviva.ai/privacy-policy/";

export function SmsConsentCheckbox({ isChecked, onChange, isDisabled }) {
  return (
    <Checkbox
      isChecked={isChecked}
      onChange={(e) => onChange(e.target.checked)}
      isDisabled={isDisabled}
      colorScheme="teal"
      alignItems="flex-start"
      data-testid="sms-consent"
    >
      <Text fontSize="sm" color="gray.600" lineHeight="short">
        Yes, text me about Viva AI demos, product news and follow-ups at this number.
        Up to 4 msgs/month. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help.
        Consent is not a condition of purchase. See our{" "}
        <Link href={TERMS_URL} isExternal color="teal.600">Terms</Link> and{" "}
        <Link href={PRIVACY_URL} isExternal color="teal.600">Privacy Notice</Link>.
      </Text>
    </Checkbox>
  );
}

export default function Signup() {
  const toast = useToast();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [cellPhoneNumber, setCellPhoneNumber] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [practiceManagementSoftware, setPracticeManagementSoftware] = useState("");
  const [otherSoftwareName, setOtherSoftwareName] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [businessId, setBusinessId] = useState(null);
  const [mapImageUrl, setMapImageUrl] = useState("");
  
  const [isLead, setIsLead] = useState(false)
  const [calendly, setCalendly] = useState(null)

  // State for alerts
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertStatus, setAlertStatus] = useState("error");

  // State for submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // State for modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);
  const [modalError, setModalError] = useState("");

  // State for modal form fields
  const [interestReason, setInterestReason] = useState("");
  const [currentPhoneCompany, setCurrentPhoneCompany] = useState("");
  const [hasPhoneContract, setHasPhoneContract] = useState("");
  const [currentAppointmentReminders, setCurrentAppointmentReminders] = useState("");
  const {
    isOpen: isAssistanceOpen,
    onOpen: onAssistanceOpen,
    onClose: onAssistanceClose,
  } = useDisclosure();

  // State for promotion code
  const [promotionCode, setPromotionCode] = useState("");
  const [promotionStatus, setPromotionStatus] = useState(null); // 'loading', 'valid', 'error', or null
  const [promotionData, setPromotionData] = useState(null);
  const [promotionError, setPromotionError] = useState("");
  const debounceTimeoutRef = useRef(null);

  // State for controlling visibility of promotion code field
  const [isPromoCodeVisible, setIsPromoCodeVisible] = useState(false);

  // For A-dec, we don't have their support email, removing for now, changing later
  const isPartner = window.location.hostname.includes('adec') || window.location.hostname.includes('a-dec');

  const { isLoaded: isMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_MAP_API,
    libraries: ["places"],
  });

  useEffect(() => {
    if (!process.env.REACT_APP_GTM) {
      return;
    }
    TagManager.initialize({ gtmId: process.env.REACT_APP_GTM });
  }, []);

  const autocompleteRef = useRef(null);

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current.getPlace();
    const isDentalOffice = place.types && (place.types.includes("dentist") || place.types.includes("doctor") || place.types.includes("health"));

    if (!isDentalOffice) {
      toast({
        title: "Invalid selection.",
        description: "You can only select a dental office.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      // Clear the input field and map
      setInputValue("");
      setSelectedPlace(null);
      setMapImageUrl("");
      return;
    }

    setSelectedPlace(place);
    setInputValue(place.name);

    // Generate the map image URL with higher zoom and smaller size
    const location = place.geometry.location;
    const lat = location.lat();
    const lng = location.lng();
    const mapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=17&size=600x200&markers=color:red%7C${lat},${lng}&key=${process.env.REACT_APP_MAP_API}`;
    setMapImageUrl(mapUrl);
  };

  const handleSoftwareChange = (e) => {
    setPracticeManagementSoftware(e.target.value);
    if (e.target.value !== "Other") {
      setOtherSoftwareName("");
    }
  };

  const isEmailValid = (email) => {
    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isFullNameValid = (name) => {
    return name.trim().split(" ").length >= 2;
  };

  const isPhoneNumberValid = (phoneNumber) => {
    const phoneRegex = /^\d{10}$/; // Validates a 10-digit number
    return phoneRegex.test(phoneNumber);
  };

  const showAlert = (message, status = "error") => {
    setAlertMessage(message);
    setAlertStatus(status);
    setIsAlertVisible(true);
  };

  const hideAlert = () => {
    setIsAlertVisible(false);
    setAlertMessage("");
  };

  const customSignUp = async (event) => {
    event.preventDefault();

    // Validation
    if (!isFullNameValid(fullName)) {
      showAlert("Please enter a valid full name.");
      return;
    }
    if (!isEmailValid(email)) {
      showAlert("Please enter a valid email address.");
      return;
    }
    if (!isPhoneNumberValid(cellPhoneNumber)) {
      showAlert("Please enter a valid 10-digit cell phone number.");
      return;
    }
    if (!selectedPlace) {
      showAlert("Please select your office from the autocomplete.");
      return;
    }
    if (!practiceManagementSoftware) {
      showAlert("Please select a practice management software.");
      return;
    }
    if (practiceManagementSoftware === "Other" && !otherSoftwareName) {
      showAlert("Please enter the name of your practice management software.");
      return;
    }
    if (promotionCode && promotionStatus !== "valid") {
      showAlert("Please enter a valid promotion code or leave it blank.");
      return;
    }

    hideAlert();
    setIsSubmitting(true);

    // Prepare data to send
    const data = {
      name: fullName,
      email,
      phone: cellPhoneNumber,
      place: selectedPlace,
      pms:
        practiceManagementSoftware === "Other"
          ? otherSoftwareName
          : practiceManagementSoftware,
      promo: promotionData?.id || undefined,
      // Partner sign-ups never see the box, so they never send it.
      ...(!isPartner && smsConsent
        ? { sms_consent: true, sms_consent_version: SMS_CONSENT_VERSION, form_url: window.location.href.split("?")[0] }
        : {}),
    };

    try {
      const response = await cqAPI.post("/signup", data);

      // Handle response
      if (response.type === "error") {
        showAlert(response.msg);
        setIsSubmitting(false);
        return;
      }
      if (process.env.REACT_APP_GTM) {
        TagManager.dataLayer({
          dataLayer: {
            event: "vivaSignup",
          },
        });
      }
      if (response.redirect){
        window.location.href = response.redirect;
        return;
      }
      if (calendly){
        setTimeout(()=>{
          window.location.href = calendly
        }, 1000)
        return;
      }
      // Successful submission of first step
      setIsSubmitting(false);
      setIsModalOpen(true); // Open the modal
      setBusinessId(response.data);
    } catch (error) {
      showAlert(`Error during sign up: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  const handleModalSubmit = async () => {
    // Validation
    if (!interestReason) {
      setModalError("Please select your main reason for your interest in viva.");
      return;
    }
    if (!currentPhoneCompany) {
      setModalError("Please select your current phone company.");
      return;
    }
    if (!hasPhoneContract) {
      setModalError("Please indicate whether you have a contract with your phone company.");
      return;
    }
    if (!currentAppointmentReminders) {
      setModalError(
        "Please select what you are currently using for appointment reminders."
      );
      return;
    }

    // Clear any previous error
    setModalError("");
    setIsModalSubmitting(true);

    // Prepare data to send
    const data = {
      interestReason,
      currentPhoneCompany,
      hasPhoneContract,
      currentAppointmentReminders,
      bid: businessId,
    };

    try {
      const response = await cqAPI.post("/signup-info", data);

      // Handle response
      if (response.type === "error") {
        setModalError(response.msg);
        setIsModalSubmitting(false);
        return;
      }

      // Successful submission
      setIsModalSubmitting(false);
      setIsModalOpen(false);
      setIsSubmitted(true);
    } catch (error) {
      setModalError(`Error during submission: ${error.message}`);
      setIsModalSubmitting(false);
    }
  };

  // Promotion Code Verification
  useEffect(() => {
    if (!promotionCode) {
      setPromotionStatus(null);
      setPromotionData(null);
      setPromotionError("");
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      verifyPromotionCode();
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [promotionCode]);

  const verifyPromotionCode = async () => {
    setPromotionStatus("loading");
    setPromotionData(null);
    setPromotionError("");
    try {
      const response = await cqAPI.get(`/verify-promo?promotion=${promotionCode}`);
      if (response.type === "ok") {
        setPromotionStatus("valid");
        setPromotionData(response.data);
      } else if (response.type === "error") {
        setPromotionStatus("error");
        setPromotionError(response.msg);
      } else {
        setPromotionStatus("error");
        setPromotionError("Invalid response from server.");
      }
    } catch (error) {
      setPromotionStatus("error");
      setPromotionError(`Error: ${error.message}`);
    }
  };

  // Check for 'promotion' parameter in URL and prefill the promotion code
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isLeadCollection = params.get("lead");
    const promoFromUrl = params.get("promotion");
    const calendlyParam = params.get('calendly')
    if (promoFromUrl) {
      // Sanitize the promo code
      const sanitizedPromoCode = promoFromUrl.replace(/[^a-zA-Z0-9_-]/g, "");
      setPromotionCode(sanitizedPromoCode);
      setIsPromoCodeVisible(true);
    }
    if (isLeadCollection){
      setIsLead(true)
    }
    if (calendlyParam){
      setCalendly(`https://calendly.com/${calendlyParam}`)
    }
  }, []);

  // Determine if form fields should be disabled
  const isFormDisabled = isSubmitted;

  return (
    <Box height="100dvh" overflowY="auto" bg="gray.50">
      <Box
        display="flex"
        marginY="100px"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        flex="1"
        px={4}
      >
        <Box
          width="100%"
          maxWidth={{ base: "100%", sm: "600px", md: "700px", lg: "800px" }}
          mt="auto"
          mb="auto"
        >
          {isSubmitted ? (
            <Box textAlign="center" px={4}>
              <Heading
                as="h2"
                fontSize={{ base: "24px", md: "28px", lg: "32px" }}
                fontWeight="700"
                color="teal.600"
                lineHeight="140%"
              >
                Thank You!
              </Heading>
              <Text mt={4} fontSize={{ base: "md", md: "lg" }} color="gray.600">
                Your information has been successfully submitted. Our team will
                contact you shortly to guide you through the onboarding process.
              </Text>
            </Box>
          ) : (
            <Box
              width="100%"
              borderWidth={1}
              borderRadius={8}
              boxShadow="lg"
              bg="white"
              p={8}
            >
              <LogoComponent
                isCollapsed={false}
                boxSize="5rem"
                mx="auto"
                mb={4}
              ></LogoComponent>
              <Box textAlign="center">
                
                {isLead? <>
                <Heading
                  as="h1"
                  fontSize={{ base: "20px", md: "24px", lg: "28px" }}
                  fontWeight="700"
                  color="teal.600"
                  lineHeight="140%"
                >
                  Book A Demo
                </Heading>
                  <Text
                  mt={2}
                  fontSize={{ base: "sm", md: "md" }}
                  maxWidth="600px"
                  mx="auto"
                  color="gray.600"
                >
                  Enter your details below to book a demo and discover how we can help your office save money and increase revenue.
                </Text></>: <>
                <Heading
                  as="h1"
                  fontSize={{ base: "20px", md: "24px", lg: "28px" }}
                  fontWeight="700"
                  lineHeight="140%"
                >
                  Demo Sign Up
                </Heading>
                  <Text
                  mt={2}
                  pt={25} 
                  pb={25}
                  fontSize={{ base: "sm", md: "md" }}
                  maxWidth="600px"
                  mx="auto"
                  color="gray.600"
                >
Thank you for Choosing Viva AI. Please provide your details to book your demo—after submitting, you’ll pick a date and time for a video meeting.
                </Text></>}
              </Box>
              <Box my={6} textAlign="left">
                <form onSubmit={customSignUp}>
                  <Grid
                    templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                    gap={6}
                  >
                    <GridItem>
                      <FormControl isRequired isDisabled={isFormDisabled}>
                        <FormLabel>Full Name</FormLabel>
                        <Input
                          type="text"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          focusBorderColor="teal.500"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl isRequired isDisabled={isFormDisabled}>
                        <FormLabel>Email Address</FormLabel>
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          focusBorderColor="teal.500"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl isRequired isDisabled={isFormDisabled}>
                        <FormLabel>Cell Phone Number</FormLabel>
                        <Input
                          type="tel"
                          value={cellPhoneNumber}
                          onChange={(e) => setCellPhoneNumber(e.target.value)}
                          focusBorderColor="teal.500"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl isRequired isDisabled={isFormDisabled}>
                        <FormLabel>Office Name</FormLabel>
                        {isMapsLoaded && (
                          <Autocomplete
                            onLoad={(autocomplete) => {
                              autocompleteRef.current = autocomplete;
                            }}
                            onPlaceChanged={handlePlaceSelect}
                          >
                            <Input
                              type="text"
                              placeholder="Type your office name and address"
                              value={inputValue}
                              onChange={(e) => setInputValue(e.target.value)}
                              focusBorderColor="teal.500"
                              isDisabled={isFormDisabled}
                            />
                          </Autocomplete>
                        )}
                        {!isMapsLoaded && (
                          <Input
                            type="text"
                            placeholder="Loading Maps..."
                            disabled
                            focusBorderColor="teal.500"
                          />
                        )}
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl isRequired isDisabled={isFormDisabled}>
                        <FormLabel>Practice Management Software</FormLabel>
                        <Select
                          value={practiceManagementSoftware}
                          onChange={handleSoftwareChange}
                          focusBorderColor="teal.500"
                        >
                          <option value="">Select an option</option>
                          <option value="Dentrix">Dentrix</option>
                          <option value="Open Dental">Open Dental</option>
                          <option value="EagleSoft">EagleSoft</option>
                          <option value="Denticon">Denticon</option>
                          <option value="Other">Other</option>
                        </Select>
                      </FormControl>
                    </GridItem>
                    {practiceManagementSoftware === "Other" && (
                      <GridItem>
                        <FormControl isRequired isDisabled={isFormDisabled}>
                          <FormLabel>Please Specify</FormLabel>
                          <Input
                            type="text"
                            value={otherSoftwareName}
                            onChange={(e) => setOtherSoftwareName(e.target.value)}
                            focusBorderColor="teal.500"
                          />
                        </FormControl>
                      </GridItem>
                    )}
                    {practiceManagementSoftware !== "Other" && (
                      <GridItem>
                        <Box></Box>
                      </GridItem>
                    )}
                    {!isPartner && (
                      <GridItem colSpan={{ base: 1, md: 2 }}>
                        <SmsConsentCheckbox
                          isChecked={smsConsent}
                          onChange={setSmsConsent}
                          isDisabled={isFormDisabled}
                        />
                      </GridItem>
                    )}
                    {!isLead && <GridItem colSpan={{ base: 1, md: 2 }}>
                      {!isPromoCodeVisible && (
                        <Button
                          variant="link"
                          onClick={() => setIsPromoCodeVisible(true)}
                          leftIcon={<AddIcon />}
                          mt={4}
                        >
                          Add Promotion Code
                        </Button>
                      )}
                      {isPromoCodeVisible && (
                        <FormControl isDisabled={isFormDisabled}>
                          <FormLabel>Promotion Code</FormLabel>
                          <Input
                            type="text"
                            value={promotionCode}
                            onChange={(e) => setPromotionCode(e.target.value)}
                            focusBorderColor="teal.500"
                          />
                          {promotionStatus === "loading" && (
                            <Text color="gray.500" fontSize="sm">
                              Validating promotion code...
                            </Text>
                          )}
                          {promotionStatus === "valid" && promotionData && (
                            <Text color="green.500" fontSize="sm">
                              Promotion applied: {promotionData.name} -{" "}
                              {promotionData.percent_off
                                ? `${promotionData.percent_off}% off`
                                : ""}
                              {promotionData.amount_off
                                ? `$${promotionData.amount_off} off`
                                : ""}{" "}
                              {promotionData.products && promotionData.products.length > 0
                                ? `of ${promotionData.products
                                    .map((product) => product.name)
                                    .join(", ")}`
                                : ""}
                              {promotionData.duration_in_months
                                ? ` for ${promotionData.duration_in_months} months`
                                : ""}
                            </Text>
                          )}
                          {promotionStatus === "error" && promotionError && (
                            <Text color="red.500" fontSize="sm">
                              {promotionError}
                            </Text>
                          )}
                        </FormControl>
                      )}
                      </GridItem>}
                    {/* Display the map if a valid place is selected */}
                    {mapImageUrl && (
                      <GridItem colSpan={{ base: 1, md: 2 }}>
                        <Box mt={4} textAlign="center">
                          <Image
                            src={mapImageUrl}
                            alt="Selected Place"
                            borderRadius="md"
                            width="100%"
                          />
                        </Box>
                      </GridItem>
                    )}
                  </Grid>
                  {isAlertVisible && (
                    <Alert status={alertStatus} mt={4} borderRadius="md">
                      <AlertIcon />
                      <Box flex="1">
                        <AlertTitle>
                          <Text casing="capitalize">{alertStatus}</Text>
                        </AlertTitle>
                        <AlertDescription display="block">
                          {alertMessage}
                        </AlertDescription>
                      </Box>
                      <CloseButton
                        position="absolute"
                        right="8px"
                        top="8px"
                        onClick={hideAlert}
                      />
                    </Alert>
                  )}
                  {!isSubmitted && (
                    <Flex justifyContent="center" mt={6}>
                      <Button
                        colorScheme="teal"
                        width="240px"
                        type="submit"
                        isLoading={isSubmitting}
                        isDisabled={isSubmitting}
                      >
                        {isLead? 'Submit': 'Sign Up'}
                      </Button>
                    </Flex>
                  )}
                </form>
              </Box>
              <Box alignItems="center" justifyItems="center">
                {!isPartner && <Flex justifyContent="center">
                  <Button
                    backgroundColor="transparent"
                    variant="contained"
                    color="teal.600"
                    onClick={onAssistanceOpen}
                  >
                    I need assistance
                  </Button>
                </Flex>}

                <Modal
                  isOpen={isAssistanceOpen}
                  onClose={onAssistanceClose}
                  isCentered
                  motionPreset='slideInBottom'
                >
                  <ModalOverlay/>
                  <ModalContent mx={8}>
                    <ModalHeader></ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody>
                      <Flex direction="column" spacing={6} alignItems="center" textAlign="center" my={4}>
                        <Text fontWeight="400">Please email us and someone from our team will get back to you.</Text>
                        <Button
                          leftIcon={<EmailIcon/>}
                          as="a"
                          href="mailto:info@getviva.ai?subject=Sign%20Up%20Inquiry"
                          my={6}
                          width="220px"
                        >
                          info@getviva.ai
                        </Button>
                      </Flex>
                    </ModalBody>

                  </ModalContent>
                </Modal>
              </Box>

            </Box>
          )}
        </Box>
      </Box>

      {/* Modal for additional information */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {}}
        isCentered
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center">
            Please provide additional information
          </ModalHeader>
          <ModalBody>
            <FormControl isRequired mt={4}>
              <FormLabel>
                What's your main reason for your interest in our service
              </FormLabel>
              <Select
                placeholder="Select an option"
                value={interestReason}
                onChange={(e) => setInterestReason(e.target.value)}
                focusBorderColor="teal.500"
              >
                <option value="Automated Call/Text Answering">
                  Automated Call/Text Answering
                </option>
                <option value="All in one solution">All in one solution</option>
                <option value="Other">Other</option>
              </Select>
            </FormControl>

            <FormControl isRequired mt={4}>
              <FormLabel>What's your current phone company?</FormLabel>
              <Select
                placeholder="Select an option"
                value={currentPhoneCompany}
                onChange={(e) => setCurrentPhoneCompany(e.target.value)}
                focusBorderColor="teal.500"
              >
                <option value="Comcast">Comcast</option>
                <option value="Verizon">Verizon</option>
                <option value="Weave">Weave</option>
                <option value="Mongo">Mongo</option>
                <option value="Other">Other</option>
              </Select>
            </FormControl>

            <FormControl isRequired mt={4}>
              <FormLabel>
                Do you have a contract with your phone company?
              </FormLabel>
              <RadioGroup
                onChange={setHasPhoneContract}
                value={hasPhoneContract}
              >
                <Stack direction="row">
                  <Radio value="Yes">Yes</Radio>
                  <Radio value="No">No</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            <FormControl isRequired mt={4}>
              <FormLabel>
                What are you currently using for appointment reminders?
              </FormLabel>
              <Select
                placeholder="Select an option"
                value={currentAppointmentReminders}
                onChange={(e) => setCurrentAppointmentReminders(e.target.value)}
                focusBorderColor="teal.500"
              >
                <option value="DentalIntel">DentalIntel</option>
                <option value="Weave">Weave</option>
                <option value="Manual">Manual</option>
                <option value="Other">Other</option>
              </Select>
            </FormControl>

            {/* Show modal error if any */}
            {modalError && (
              <Alert status="error" mt={4} borderRadius="md">
                <AlertIcon />
                <Box flex="1">
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription display="block">{modalError}</AlertDescription>
                </Box>
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="teal"
              width="100%"
              onClick={handleModalSubmit}
              isLoading={isModalSubmitting}
              isDisabled={isModalSubmitting}
            >
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
